import { z } from "zod";

const solutions = ["reorder", "return_and_reorder", "coupon", "refund", "report", "other"] as const;

export const subjects = [
	"order_product_complaint",
	"order_product_not_delivered",
	"order_product_return",
	"order_delivery",
] as const;

const productTypes = ["sheet", "cabinet", "sample", "accessory", "product"] as const;

export const sendThisToHemlockSchema = z
	.object({
		subject: z.enum(subjects),
		increment_id: z.string(),
		email: z.string().email().min(1),
		description: z.string().optional(),
		product: z
			.object({
				order_item_id: z.string(),
				panel_id: z.string().optional(),
				type: z.enum(productTypes),
				issue: z
					.object({
						type: z.string().optional(),
						sub_type: z.string().optional(),
						description: z.string().optional(),
					})
					.optional(),
				solution: z
					.object({
						type: z.enum(solutions).optional(),
						amount: z.number().optional(),
						description: z.string().optional(),
					})
					.optional(),
				files: z
					.array(z.custom<File>((v) => v instanceof File))
					.min(1)
					.optional(),
			})
			.array()
			.min(1),
	})
	.superRefine((data, context) => {
		if (data.product.filter((product) => product.issue?.type).length === 0) {
			context.addIssue({
				code: z.ZodIssueCode.custom,
				params: { type: "contact_issue", minimum: 1 },
				path: ["product"],
			});
		}

		data.product.forEach((product, index) => {
			if (product.issue?.type === "other" && !product.issue.description) {
				context.addIssue({
					code: z.ZodIssueCode.too_small,
					type: "string",
					inclusive: true,
					minimum: 1,
					path: ["product", index, "issue", "description"],
				});
			}

			if (product.solution?.type === "other" && !product.solution.description) {
				context.addIssue({
					code: z.ZodIssueCode.too_small,
					type: "string",
					inclusive: true,
					minimum: 1,
					path: ["product", index, "solution", "description"],
				});
			}
		});
	});

export type HemlockIssueType =
	| "damage"
	| "production_error"
	| "incorrect_material"
	| "quality"
	| "product_not_received"
	| "rest_material_not_received"
	| "other_product_received"
	| "other"
	| "general_quality"
	| "dimensions_incorrect"
	| "finishing_missing"
	| "finishing_incorrect"
	| "installation_box_not_complete"
	| "installation_box_not_received";

export type HemlockSubIssueType =
	| "dimensions_incorrect"
	| "finishing_missing"
	| "finishing_incorrect"
	| "shape_incorrect"
	| "cutting_quality"
	| "squareness"
	| "warped"
	| "visual_quality"
	| "color_difference"
	| "general_quality";

export type HemlockSolutionType = (typeof solutions)[number];
export type HemlockSubjectType = (typeof subjects)[number];
export type HemlockProductType = (typeof productTypes)[number];
